import { create } from 'zustand';
import { cartService } from '@/features/cart/services/cartService';

const CART_COUNT_STORAGE_KEY = 'nightsync_cart_count';

interface CartStore {
  cartCount: number | null;
  isLoading: boolean;
  error: string | null;
  fetchCartCount: () => Promise<void>;
  resetCartCount: () => void;
  clearCacheAndRefetch: () => Promise<void>;
}

/**
 * Get cart count from localStorage
 */
const getStoredCartCount = (): number | null => {
  try {
    const stored = localStorage.getItem(CART_COUNT_STORAGE_KEY);
    if (stored !== null) {
      const count = parseInt(stored, 10);
      return isNaN(count) ? null : count;
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * Store cart count in localStorage
 */
const storeCartCount = (count: number): void => {
  try {
    localStorage.setItem(CART_COUNT_STORAGE_KEY, count.toString());
  } catch {
    // Ignore storage errors
  }
};

/**
 * Clear cart count from localStorage
 */
const clearStoredCartCount = (): void => {
  try {
    localStorage.removeItem(CART_COUNT_STORAGE_KEY);
  } catch {
    // Ignore storage errors
  }
};

export const useCartStore = create<CartStore>((set, get) => ({
  cartCount: null,
  isLoading: false,
  error: null,

  fetchCartCount: async () => {
    // Don't fetch if already loading
    if (get().isLoading) return;

    // Step 1: Show localStorage value immediately (stale)
    const storedCount = getStoredCartCount();
    if (storedCount !== null) {
      set({ cartCount: storedCount });
    }

    // Step 2: ALWAYS fetch from API to revalidate
    set({ isLoading: true, error: null });
    try {
      const count = await cartService.getIncompleteCartsCount();
      // Step 3: Only update if different to avoid unnecessary re-renders
      if (count !== get().cartCount) {
        storeCartCount(count);
        set({ cartCount: count });
      }
      set({ isLoading: false, error: null });
    } catch (error) {
      console.error('Error fetching cart count:', error);
      // Don't set error for 401 (unauthorized) - user just isn't logged in
      const axiosError = error as { response?: { status?: number } };
      if (axiosError?.response?.status === 401) {
        set({ cartCount: 0, isLoading: false, error: null });
      } else {
        set({
          cartCount: null,
          isLoading: false,
          error: 'Failed to fetch cart count'
        });
      }
    }
  },

  resetCartCount: () => {
    clearStoredCartCount();
    set({ cartCount: null });
  },

  /**
   * Clear localStorage cache and force refetch from API
   * Use after cart operations (add, delete, payment) to get fresh count
   */
  clearCacheAndRefetch: async () => {
    clearStoredCartCount();
    // Reset loading state to allow refetch
    set({ isLoading: false });
    await get().fetchCartCount();
  },
}));
